import React, { useState } from 'react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import {
  BarChart3,
  TrendingUp,
  Play,
  Loader2,
  AlertCircle,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';
import api from '../services/api';

interface AnalysisResult {
  symbol: string;
  timestamp: string;
  timeframe: string;
  technicalAnalysis: {
    indicators: any;
    patterns: any;
    trendDirection: string;
    trendStrength: number;
    keyLevels: any[];
  };
  fundamentalAnalysis: {
    ratios: any;
    valuation: any;
    summary: any;
  };
  combinedScore: number;
  recommendation: 'buy' | 'sell' | 'hold';
  confidence: number;
  reasoning: string[];
}

interface BatchAnalysisResult {
  results: AnalysisResult[];
  errors: string[];
}

const Analysis: React.FC = () => {
  const [symbol, setSymbol] = useState('');
  const [timeframe, setTimeframe] = useState<
    '1m' | '5m' | '15m' | '30m' | '1d'
  >('5m');
  const [days, setDays] = useState(5);
  const [analysisType, setAnalysisType] = useState<'intraday' | 'daily'>(
    'intraday',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentResult, setCurrentResult] = useState<AnalysisResult | null>(
    null,
  );
  const [batchSymbols, setBatchSymbols] = useState('');
  const [batchResults, setBatchResults] = useState<BatchAnalysisResult | null>(
    null,
  );
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [showFundamentalDetails, setShowFundamentalDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'single' | 'batch'>('single');

  const handleSingleAnalysis = async () => {
    if (!symbol.trim()) {
      setError('Please enter a symbol');
      return;
    }

    setIsLoading(true);
    setError(null);
    setCurrentResult(null);

    try {
      let result: AnalysisResult;

      if (analysisType === 'intraday') {
        result = await api.analyzeTickerIntraday(
          { symbol: symbol.toUpperCase() },
          { timeframe, days },
        );
      } else {
        result = await api.analyzeTickerDaily({ symbol: symbol.toUpperCase() });
      }

      setCurrentResult(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Analysis failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchAnalysis = async () => {
    if (!batchSymbols.trim()) {
      setError('Please enter symbols');
      return;
    }

    const symbols = batchSymbols
      .split(/[,\s]+/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length > 0);

    if (symbols.length === 0) {
      setError('Please enter valid symbols');
      return;
    }

    setIsLoading(true);
    setError(null);
    setBatchResults(null);

    try {
      const result = await api.batchAnalysis({
        symbols,
        timeframe: analysisType === 'intraday' ? timeframe : '1d',
        includeFundamentals: analysisType === 'daily',
      });

      setBatchResults(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Batch analysis failed');
    } finally {
      setIsLoading(false);
    }
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'buy':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sell':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'hold':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatScore = (score: number) => {
    return Math.round(score);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TierPageBanner
          title="Analysis · tier relevance"
          description="Bulk analysis supports orchestrator candidate pools—most active in Tier 6 options today."
          thesisTimeframe="1_2_weeks"
        />
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Market Analysis
              </h1>
              <p className="mt-2 text-gray-600">
                Perform technical and fundamental analysis on stocks with
                real-time data
              </p>
            </div>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <RefreshCw className="h-5 w-5 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('single')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'single'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Single Analysis
              </button>
              <button
                onClick={() => setActiveTab('batch')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'batch'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Batch Analysis
              </button>
            </nav>
          </div>
        </div>

        {activeTab === 'single' && (
          <div className="space-y-6">
            {/* Single Analysis Form */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-4">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Single Ticker Analysis
                </h2>
                <p className="text-sm text-gray-600">
                  Analyze a single ticker with technical and fundamental
                  indicators
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Symbol
                  </label>
                  <input
                    type="text"
                    placeholder="AAPL"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Analysis Type
                  </label>
                  <select
                    value={analysisType}
                    onChange={(e) =>
                      setAnalysisType(e.target.value as 'intraday' | 'daily')
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="intraday">Intraday</option>
                    <option value="daily">Daily</option>
                  </select>
                </div>

                {analysisType === 'intraday' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timeframe
                      </label>
                      <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="1m">1 Minute</option>
                        <option value="5m">5 Minutes</option>
                        <option value="15m">15 Minutes</option>
                        <option value="30m">30 Minutes</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Days
                      </label>
                      <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={1}>1 Day</option>
                        <option value={3}>3 Days</option>
                        <option value={5}>5 Days</option>
                        <option value={10}>10 Days</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={handleSingleAnalysis}
                disabled={isLoading || !symbol.trim()}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Run Analysis
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Analysis Results */}
            {currentResult && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-medium text-gray-900 flex items-center">
                        <TrendingUp className="h-5 w-5 mr-2" />
                        Analysis Results: {currentResult.symbol}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {formatTimestamp(currentResult.timestamp)} •{' '}
                        {currentResult.timeframe} timeframe
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRecommendationColor(currentResult.recommendation)}`}
                      >
                        {currentResult.recommendation.toUpperCase()}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                        {formatScore(currentResult.combinedScore)}/100
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Summary Section */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">
                        {formatScore(currentResult.combinedScore)}
                      </div>
                      <div className="text-sm text-gray-600">
                        Combined Score
                      </div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div
                        className={`text-2xl font-bold ${getConfidenceColor(currentResult.confidence)}`}
                      >
                        {formatScore(currentResult.confidence)}%
                      </div>
                      <div className="text-sm text-gray-600">Confidence</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">
                        {currentResult.technicalAnalysis.trendDirection}
                      </div>
                      <div className="text-sm text-gray-600">
                        Trend Direction
                      </div>
                    </div>
                  </div>

                  {/* Reasoning */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      Analysis Reasoning
                    </h3>
                    <div className="space-y-2">
                      {currentResult.reasoning.map((reason, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-gray-700">{reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <hr className="border-gray-200" />

                  {/* Technical Analysis */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold">
                        Technical Analysis
                      </h3>
                      <button
                        onClick={() =>
                          setShowTechnicalDetails(!showTechnicalDetails)
                        }
                        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {showTechnicalDetails ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-2" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            Show Details
                          </>
                        )}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          Trend Strength
                        </div>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${currentResult.technicalAnalysis.trendStrength}%`,
                            }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatScore(
                            currentResult.technicalAnalysis.trendStrength,
                          )}
                          /100
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          Key Levels
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {currentResult.technicalAnalysis.keyLevels.length}{' '}
                          levels identified
                        </div>
                      </div>
                    </div>

                    {showTechnicalDetails && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <pre className="text-xs text-gray-700 overflow-auto">
                          {JSON.stringify(
                            currentResult.technicalAnalysis.indicators,
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    )}
                  </div>

                  {/* Fundamental Analysis (only for daily) */}
                  {analysisType === 'daily' &&
                    currentResult.fundamentalAnalysis.ratios && (
                      <>
                        <hr className="border-gray-200" />
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">
                              Fundamental Analysis
                            </h3>
                            <button
                              onClick={() =>
                                setShowFundamentalDetails(
                                  !showFundamentalDetails,
                                )
                              }
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              {showFundamentalDetails ? (
                                <>
                                  <EyeOff className="h-4 w-4 mr-2" />
                                  Hide Details
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Show Details
                                </>
                              )}
                            </button>
                          </div>

                          {showFundamentalDetails && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                              <pre className="text-xs text-gray-700 overflow-auto">
                                {JSON.stringify(
                                  currentResult.fundamentalAnalysis,
                                  null,
                                  2,
                                )}
                              </pre>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'batch' && (
          <div className="space-y-6">
            {/* Batch Analysis Form */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-4">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Batch Analysis
                </h2>
                <p className="text-sm text-gray-600">
                  Analyze multiple tickers simultaneously
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Symbols (comma-separated)
                  </label>
                  <textarea
                    placeholder="AAPL, MSFT, GOOGL, TSLA"
                    value={batchSymbols}
                    onChange={(e) => setBatchSymbols(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Analysis Type
                    </label>
                    <select
                      value={analysisType}
                      onChange={(e) =>
                        setAnalysisType(e.target.value as 'intraday' | 'daily')
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="intraday">Intraday</option>
                      <option value="daily">Daily</option>
                    </select>
                  </div>

                  {analysisType === 'intraday' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timeframe
                      </label>
                      <select
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value as any)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="5m">5 Minutes</option>
                        <option value="15m">15 Minutes</option>
                        <option value="30m">30 Minutes</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleBatchAnalysis}
                disabled={isLoading || !batchSymbols.trim()}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Run Batch Analysis
                  </>
                )}
              </button>
            </div>

            {/* Batch Results */}
            {batchResults && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">
                    Batch Analysis Results
                  </h2>
                  <p className="text-sm text-gray-600">
                    {batchResults.results.length} successful,{' '}
                    {batchResults.errors.length} errors
                  </p>
                </div>

                <div className="p-6 space-y-4">
                  {/* Results */}
                  {batchResults.results.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        Analysis Results
                      </h3>
                      <div className="space-y-2">
                        {batchResults.results.map((result, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center space-x-3">
                              <span className="font-medium">
                                {result.symbol}
                              </span>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRecommendationColor(result.recommendation)}`}
                              >
                                {result.recommendation.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-600">
                                Score: {formatScore(result.combinedScore)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatTimestamp(result.timestamp)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Errors */}
                  {batchResults.errors.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-red-600">
                        Errors
                      </h3>
                      <div className="space-y-2">
                        {batchResults.errors.map((error, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg"
                          >
                            <XCircle className="h-4 w-4 text-red-500" />
                            <span className="text-sm text-red-700">
                              {error}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Analysis;
